import React, { useState } from 'react';
import { t } from '@lingui/macro';

import { EditSmall, Home } from '@rover/icons';
import { Box, Text } from '@rover/kibble/core';
import Row from '@rover/kibble/patterns/Row';
import { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest';
import emitMetric from '@rover/utilities/emitMetric';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import useHomeAddress from '../hooks/useHomeAddress';
import { formatAddress } from '../utils';

import HomeEditModal from './HomeEditModal';
import OptionLabel from './OptionLabel';

type Props = {
  onEdit: (newAddressData: string) => void;
};

export default function HomeAddressOption({ onEdit }: Props): JSX.Element {
  const { formattedAddress } = useHomeAddress();
  const [isHomeAddressUpdateModalOpen, setIsHomeAddressUpdateModalOpen] = useState(false);

  const onClickEdit = (e): void => {
    e.stopPropagation();
    emitMetric('search-edit-home-address-click', { source: 'web' });
    fireDataLayerEvent({ event: 'search-edit-home-address-click' });
    setIsHomeAddressUpdateModalOpen(true);
  };

  const onCloseModal = (): void => {
    setIsHomeAddressUpdateModalOpen(false);
  };

  return (
    <OptionLabel title={t`Home address`}>
      <Row
        ariaLabel={t`Home address`}
        label={t`Home address`}
        sublabel={
          <Text
            size="100"
            sx={{
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
            textColor="tertiary"
          >
            {formattedAddress || t`None`}
          </Text>
        }
        leftItem={<Home width={24} height={24} />}
        rightItem={
          <Box onClick={onClickEdit}>
            <EditSmall width={18} height={18} />
          </Box>
        }
      />
      <HomeEditModal
        isOpen={isHomeAddressUpdateModalOpen}
        onClose={onCloseModal}
        onEdit={(newAddressData: UserInformationAddress) => {
          const newFormattedAddress = formatAddress(newAddressData);
          onEdit(newFormattedAddress);
        }}
      />
    </OptionLabel>
  );
}
